import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  useDbHealth,
  useDbSchemas,
  useDbTables,
  useSyncState,
  useOutboxSummary,
  useTenants,
} from '../hooks/api';
import { Loading, ErrorBox, EmptyBox } from '../components/Loading';
import { Status, statusKindForJobStatus } from '../components/Status';
import { formatBytes, formatNumber, formatTimestamp } from '../api';
import { IntegrationsPanel } from './IntegrationsPage';

type Tab = 'db' | 'integrations' | 'sync' | 'outbox' | 'webhooks' | 'audit';

export default function TenantDetailPage() {
  const { tenantCode } = useParams<{ tenantCode: string }>();
  const [tab, setTab] = useState<Tab>('db');
  const tenants = useTenants();
  const tenant = tenants.data?.data.find((t) => t.code === tenantCode);
  const health = useDbHealth(tenantCode);

  if (!tenantCode) return <ErrorBox error="kein tenantCode" />;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between border-b border-neutral-300 pb-1">
        <div>
          <h1 className="text-[14px] font-semibold uppercase tracking-wide text-neutral-700">
            Tenant: <span className="font-mono normal-case">{tenantCode}</span>
          </h1>
          {tenant && (
            <div className="text-[11px] text-neutral-600 font-mono">
              {tenant.name} · DB: {tenant.database_name}
            </div>
          )}
        </div>
        <Link to="/tenants" className="btn">
          ← Zur Liste
        </Link>
      </div>

      {/* Header-KPIs */}
      <div className="grid grid-cols-4 gap-3">
        <KpiPanel title="Status">
          {tenant?.active ? <Status kind="ok">aktiv</Status> : <Status kind="muted">inaktiv</Status>}
        </KpiPanel>
        <KpiPanel title="DB-Groesse">
          <span className="font-mono">{health.data ? formatBytes(health.data.size_bytes) : '–'}</span>
        </KpiPanel>
        <KpiPanel title="Connections">
          <span className="font-mono">{health.data?.connections ?? '–'}</span>
        </KpiPanel>
        <KpiPanel title="Aelteste TX">
          <span className="font-mono text-[11px]">
            {health.data?.oldest_active_xact ? formatTimestamp(health.data.oldest_active_xact) : '–'}
          </span>
        </KpiPanel>
      </div>

      {/* Tab-Navigation (klassisch ERP) */}
      <div className="flex border-b border-neutral-300">
        {(
          [
            ['db', 'DB-Schema'],
            ['integrations', 'Integrationen'],
            ['sync', 'Sync-State'],
            ['outbox', 'Outbox'],
            ['webhooks', 'Webhooks'],
            ['audit', 'Audit'],
          ] as Array<[Tab, string]>
        ).map(([k, label]) => (
          <button
            key={k}
            type="button"
            className={
              'px-3 py-1 border border-b-0 border-neutral-300 text-[12px] -mb-px ' +
              (tab === k
                ? 'bg-white text-slate-900 font-semibold border-t-2 border-t-slate-700'
                : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200')
            }
            onClick={() => setTab(k)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'db' && <DbSchemaTab tenantCode={tenantCode} />}
      {tab === 'integrations' && <IntegrationsPanel tenantCode={tenantCode} />}
      {tab === 'sync' && <SyncTab tenantCode={tenantCode} />}
      {tab === 'outbox' && <OutboxTab tenantCode={tenantCode} />}
      {tab === 'webhooks' && (
        <div className="panel">
          <div className="panel-h">Webhooks</div>
          <div className="panel-b">
            <Link to="/operations/webhooks" className="btn">
              Cross-Tenant Webhook-Activity oeffnen
            </Link>
          </div>
        </div>
      )}
      {tab === 'audit' && (
        <div className="panel">
          <div className="panel-h">Tenant-lokales Audit</div>
          <div className="panel-b">
            <p className="text-[12.5px] text-neutral-700">
              Tenant-lokales <span className="font-mono">audit.audit_log</span> wird in der naechsten
              Iteration angebunden. Cross-Tenant-Audit unter{' '}
              <Link to="/audit" className="underline">
                /audit
              </Link>
              .
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function DbSchemaTab({ tenantCode }: { tenantCode: string }) {
  const schemas = useDbSchemas(tenantCode);
  const [activeSchema, setActiveSchema] = useState<string | undefined>(undefined);
  const tables = useDbTables(tenantCode, activeSchema);

  return (
    <div className="grid grid-cols-[260px_1fr] gap-3">
      <div className="panel">
        <div className="panel-h">Schemas</div>
        <div className="panel-b p-0">
          {schemas.isLoading && (
            <div className="p-2">
              <Loading />
            </div>
          )}
          {schemas.error && (
            <div className="p-2">
              <ErrorBox error={schemas.error} />
            </div>
          )}
          {schemas.data && (
            <ul className="text-[12.5px]">
              <li>
                <button
                  type="button"
                  className={
                    'w-full text-left px-2 py-1 border-b border-neutral-200 ' +
                    (activeSchema === undefined ? 'bg-slate-100 font-semibold' : 'hover:bg-neutral-50')
                  }
                  onClick={() => setActiveSchema(undefined)}
                >
                  alle ({schemas.data.schemas.reduce((a, s) => a + s.table_count, 0)})
                </button>
              </li>
              {schemas.data.schemas.map((s) => (
                <li key={s.schema}>
                  <button
                    type="button"
                    className={
                      'w-full text-left px-2 py-1 border-b border-neutral-200 ' +
                      (activeSchema === s.schema
                        ? 'bg-slate-100 font-semibold'
                        : 'hover:bg-neutral-50')
                    }
                    onClick={() => setActiveSchema(s.schema)}
                  >
                    <div className="flex justify-between font-mono">
                      <span>{s.schema}</span>
                      <span className="text-[10px] text-neutral-500">
                        {s.table_count} · {formatBytes(s.size_bytes)}
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="panel">
        <div className="panel-h">Tabellen {activeSchema ? `(Schema: ${activeSchema})` : '(alle)'}</div>
        <div className="panel-b p-0">
          {tables.isLoading && (
            <div className="p-2">
              <Loading />
            </div>
          )}
          {tables.error && (
            <div className="p-2">
              <ErrorBox error={tables.error} />
            </div>
          )}
          {tables.data && tables.data.tables.length === 0 && (
            <div className="p-2">
              <EmptyBox>Keine Tabellen.</EmptyBox>
            </div>
          )}
          {tables.data && tables.data.tables.length > 0 && (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Schema</th>
                  <th>Tabelle</th>
                  <th className="num">Zeilen (est.)</th>
                  <th className="num">Groesse</th>
                  <th className="num">Indices</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {tables.data.tables.map((t) => (
                  <tr key={`${t.schema}.${t.table}`}>
                    <td className="mono">{t.schema}</td>
                    <td className="mono">{t.table}</td>
                    <td className="num">{formatNumber(t.row_estimate)}</td>
                    <td className="num">{formatBytes(t.total_size_bytes)}</td>
                    <td className="num">{formatBytes(t.indexes_size_bytes)}</td>
                    <td>
                      <Link
                        to={`/tenants/${encodeURIComponent(tenantCode)}/db/${t.schema}/${t.table}`}
                        className="btn"
                      >
                        Detail
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function SyncTab({ tenantCode }: { tenantCode: string }) {
  const sync = useSyncState(tenantCode);
  return (
    <div className="panel">
      <div className="panel-h">Sync-State</div>
      <div className="panel-b p-0">
        {sync.isLoading && (
          <div className="p-2">
            <Loading />
          </div>
        )}
        {sync.error && (
          <div className="p-2">
            <ErrorBox error={sync.error} />
          </div>
        )}
        {sync.data && sync.data.data.length === 0 && (
          <div className="p-2">
            <EmptyBox>Noch kein Sync-Run gelaufen.</EmptyBox>
          </div>
        )}
        {sync.data && sync.data.data.length > 0 && (
          <table className="tbl">
            <thead>
              <tr>
                <th>Source</th>
                <th>Entity</th>
                <th>Status</th>
                <th className="num">processed</th>
                <th className="num">created</th>
                <th className="num">updated</th>
                <th>Letzter Full-Sync</th>
                <th>Letzter Fehler</th>
              </tr>
            </thead>
            <tbody>
              {sync.data.data.map((r) => (
                <tr key={`${r.source_system}.${r.entity}`}>
                  <td className="mono">{r.source_system}</td>
                  <td className="mono">{r.entity}</td>
                  <td>
                    <Status kind={statusKindForJobStatus(r.status)}>{r.status}</Status>
                  </td>
                  <td className="num">{formatNumber(r.rows_processed)}</td>
                  <td className="num">{formatNumber(r.rows_created)}</td>
                  <td className="num">{formatNumber(r.rows_updated)}</td>
                  <td className="mono">{formatTimestamp(r.last_full_sync_at)}</td>
                  <td className="text-[11px] text-st-err">{r.last_error ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function OutboxTab({ tenantCode }: { tenantCode: string }) {
  const summary = useOutboxSummary(tenantCode);
  return (
    <div className="panel">
      <div className="panel-h">Outbox-Summary (last 7d + offene)</div>
      <div className="panel-b p-0">
        {summary.isLoading && (
          <div className="p-2">
            <Loading />
          </div>
        )}
        {summary.error && (
          <div className="p-2">
            <ErrorBox error={summary.error} />
          </div>
        )}
        {summary.data && summary.data.data.length === 0 && (
          <div className="p-2">
            <EmptyBox>Keine Outbox-Events.</EmptyBox>
          </div>
        )}
        {summary.data && summary.data.data.length > 0 && (
          <table className="tbl">
            <thead>
              <tr>
                <th>Target</th>
                <th>Status</th>
                <th className="num">Anzahl</th>
              </tr>
            </thead>
            <tbody>
              {summary.data.data.map((r) => (
                <tr key={`${r.target_system}.${r.status}`}>
                  <td className="mono">{r.target_system}</td>
                  <td>
                    <Status kind={statusKindForJobStatus(r.status)}>{r.status}</Status>
                  </td>
                  <td className="num">{formatNumber(r.count)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function KpiPanel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="panel">
      <div className="panel-h">{title}</div>
      <div className="panel-b">
        <div className="text-[14px]">{children}</div>
      </div>
    </div>
  );
}
